import { TrainerCapability } from '@zeus/index'
import { useTranslation } from 'react-i18next'
import { useCan } from '~/hooks/use-can'
import AlertStrip from '~/pages/backoffice/admin/dashboard/AlertStrip'
import CompletionSummary from '~/pages/backoffice/admin/dashboard/CompletionSummary'
import CompletionTrend from '~/pages/backoffice/admin/dashboard/CompletionTrend'
import StatisticsTable from '~/pages/backoffice/admin/statistics/StatisticsTable'

/**
 * Tableau de bord de l'admin et du formateur.
 *
 * Fusionne les deux écrans qui se partageaient la question « où en est-on ? » :
 * l'accueil (#201), qui listait ce qu'il y a à faire aujourd'hui, et les
 * statistiques (#111, #138), qui mesuraient l'avancement. Les avoir séparés
 * obligeait à ouvrir l'un pour comprendre l'autre.
 *
 * Trois blocs, et leur découpage tient à une distinction de PÉRIMÈTRE plus qu'à
 * une hiérarchie visuelle :
 *
 *   1. `CompletionSummary` puis `CompletionTrend` — l'accueil, les chiffres de
 *      l'organisation entière et leur évolution. Aucun filtre ne les touche :
 *      c'est le repère fixe de l'écran ;
 *   2. `AlertStrip` — ce qui appelle une action aujourd'hui. Absent quand il
 *      n'y a rien : une journée calme doit se voir, pas se meubler ;
 *   3. `StatisticsTable` — le détail apprenant par apprenant, avec SES filtres,
 *      son tri et son export.
 *
 * ⚠️ Les filtres appartiennent au bloc 3 et à lui seul. Branchés aussi sur le
 * bloc 1, ils faisaient bouger le « taux de complétion » de l'organisation dès
 * qu'on cherchait un nom : le même écran annonçait deux chiffres différents
 * selon ce qu'on avait tapé, sans dire lequel valait pour l'organisme.
 *
 * Un formateur dont l'organisation a retiré les statistiques (#11) n'a plus
 * aucun des trois blocs — les alertes aussi ne parlent que d'avancement
 * (`backOfficeHome` exige le même droit). L'écran reste son accueil, et dit
 * pourquoi il est vide.
 */
function DashboardPage() {
  const { t } = useTranslation()
  const canSeeStatistics = useCan(TrainerCapability.STATISTICS_VIEW)

  if (!canSeeStatistics) {
    return (
      <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        {t('dashboard.noStatistics')}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <CompletionSummary />
      <CompletionTrend />
      <AlertStrip />
      <StatisticsTable />
    </div>
  )
}

export default DashboardPage
